import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Aside from '../../../../components/layout/asaid.component';
import Header from '../../../../components/layout/header.component';
import style from './templates-wa.module.scss';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import Input from '../../../../components/forms/input.component';
import Icon from '../../../../components/forms/icon.component';
import Title from '../../../../components/forms/titles.component';
import Select from 'react-select';
import TextArea from '../../../../components/forms/text-area.component';
import InvalidFeedback from '../../../../components/forms/invalid-feedback.component';
import { useTranslation } from 'react-i18next';
import Button from '../../../../components/forms/button.component';
import { AlertService } from '../../../../services/alert.service';
import QuickReplyBtn from './quick-reply-btn.component';
import UrlBtn from './url-btn.component';
import useBgPages from '../../../../hooks/use-bg-pages.hook';
import { useExtractVariables } from '../../../../hooks/use-extract-variables.hook';
import ButtonsDnd from '../../../../components/forms/buttons-templates-wa.component';
import PhoneNumberBtn from './phone-btn.component';
import OfferCodeBtn from './offer-btn.component';
import { AccountsService } from '../../../../services/accounts.service';
import { CompaniesService } from '../../../../services/companies.service';
import { Dropzone } from '@dropzone-ui/react';
import Tooltip from '../../../../components/forms/tooltip.component';
import { TemplatesService } from '../../../../services/templates.service';
import Navigate from '../../../../components/cards/header-navigate.component';
import { useSession } from '../../../../hooks/use-session.hook';
import { PROFILES } from '../../../../constants';
import { useDarkMode } from '../../../../hooks/use-dark-mode.hook';
import { Payload, TransformedPayload, ButtonsData } from '../types';

function CreateTemplatesWa() {
  useBgPages();
  const { t } = useTranslation();
  const { session, selectedAccount } = useSession();
  const [imageSrc, setImageSrc] = useState(null);
  const [showAllOptions, setShowAllOptions] = useState(false);
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [accountOptions, setAccountOptions] = useState<any[]>([]);

  const languageOptions = [
    { value: 'es_MX', label: t('templates.wa.create.es') },
    { value: 'en', label: t('templates.wa.create.en') }
  ];
  const parameterFormatOptions = [
    { value: 'POSITIONAL', label: t('templates.wa.create.parameter-positional') },
    { value: 'NAMED', label: t('templates.wa.create.parameter-named') }
  ];

  const headerOptions = [
    { value: 'none', label: t('templates.wa.create.header-option-1') },
    { value: 'TEXT', label: t('templates.wa.create.header-option-2') },
    { value: 'IMAGE', label: t('templates.wa.create.header-option-3') },
    { value: 'VIDEO', label: t('templates.wa.create.header-option-4') },
    { value: 'DOCUMENT', label: t('templates.wa.create.header-option-5') },
    { value: 'LOCATION', label: t('templates.wa.create.header-option-6') }
  ];

  const defaultHeaderValues = {
    type: 'HEADER',
    format: 'none',
    text: '',
    file: '',
    error: false,
    example: {
      header_text_named_params: [
        {
          param_name: '',
          example: ''
        }
      ],
      header_handle: '',
      header_text: ['']
    }
  };
  const defaultBodyValues = {
    type: 'BODY',
    text: '',
    error: false,
    example: {
      body_text: [''],
      body_text_named_params: [
        {
          param_name: '',
          example: ''
        }
      ]
    }
  };
  const defaultFooterValues = {
    type: 'FOOTER',
    text: ''
  };

  const defaultButtonValues = {
    type: '',
    text: '',
    url: '',
    style: '',
    example: '',
    offer_code: '',
    country_code: '',
    phone_number: ''
  };
  const defaultButtonsValues: ButtonsData = {
    type: 'BUTTONS',
    buttons: []
  };
  const defaultValues = {
    name: '',
    category: 'MARKETING',
    parameter_format: 'POSITIONAL',
    language: '',
    header: defaultHeaderValues,
    body: defaultBodyValues,
    footer: defaultFooterValues,
    buttons: defaultButtonsValues,
    companies: '',
    account: ''
  };

  const {
    register,
    watch,
    control,
    resetField,
    setValue,
    handleSubmit,
    trigger,
    formState: { errors, isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues
  });

  const buttonActionsArray: any = useFieldArray({
    control,
    name: 'buttons.buttons' as any
  });

  const navigate = useNavigate();
  const header = watch('header');
  const headerText = watch('header.text');
  const body = watch('body.text');
  const footer = watch('footer.text');
  const buttons = watch('buttons');
  const location = useLocation();
  const wabaId = location.state?.wabaId;
  const accountName = location.state?.accountName;
  const isDarkTheme = useDarkMode();

  const parameterFormat = watch('parameter_format') as 'POSITIONAL' | 'NAMED';
  const headerVars = useFieldArray({
    control,
    name: 'header.example.header_text_named_params'
  });
  const headerNamedParams = useWatch({
    control,
    name: 'header.example.header_text_named_params'
  });

  const bodyVars = useFieldArray({
    control,
    name: 'body.example.body_text_named_params' as never
  });

  const bodyNamedParams = useWatch<any>({
    control,
    name: 'body.example.body_text_named_params'
  });

  const setHeaderError = useCallback(
    (error: boolean) => setValue('header.error', error),
    [setValue]
  );
  const setBodyError = useCallback((error: boolean) => setValue('body.error', error), [setValue]);
  const updateHeaderVars = useCallback((vars: any) => headerVars.replace(vars), [headerVars]);
  const updateBodyVars = useCallback((vars: any) => bodyVars.replace(vars), [bodyVars]);
  const showVariables = !errors.header?.text && headerVars.fields.length > 0;
  const bodyShowVariables = !errors.body?.text && bodyVars.fields.length > 0;

  useExtractVariables({
    text: headerText,
    parameterFormat,
    setError: setHeaderError,
    setVariables: updateHeaderVars,
    maxVariables: 1
  });

  useExtractVariables({
    text: body,
    parameterFormat,
    setError: setBodyError,
    setVariables: updateBodyVars
  });

  const cleanHeader = (header: any) => {
    if (!header) return undefined;

    const { type, format, text, file, example } = header;
    const cleanedHeader: any = { type, format };

    if (file) {
      cleanedHeader.file = file;
    }

    if (format === 'TEXT') {
      cleanedHeader.text = text;
      cleanedHeader.example = {};
      if (parameterFormat === 'POSITIONAL') {
        if (example?.header_text?.length > 0) {
          const filtered = example.header_text.filter((val: string) => val?.trim() !== '');
          if (filtered.length > 0) {
            cleanedHeader.example.header_text = filtered;
          }
        }
      }

      if (parameterFormat === 'NAMED') {
        if (
          example?.header_text_named_params?.some(
            (param: any) => param?.param_name?.trim() !== '' && param?.example?.trim() !== ''
          )
        ) {
          cleanedHeader.example.header_text_named_params = example.header_text_named_params.filter(
            (param: any) => param?.param_name?.trim() !== '' && param?.example?.trim() !== ''
          );
        }
      }
      if (Object.keys(cleanedHeader.example).length === 0) {
        delete cleanedHeader.example;
      }
    } else if (['IMAGE', 'VIDEO', 'DOCUMENT', 'LOCATION'].includes(format)) {
      const hasHeaderHandle =
        example &&
        Array.isArray(example.header_handle) &&
        example.header_handle.some((id: any) => id && id.trim() !== '');

      if (hasHeaderHandle) {
        cleanedHeader.example = {
          header_handle: example.header_handle.filter((id: any) => id && id.trim() !== '')
        };
      }
    }

    return cleanedHeader;
  };

  const cleanPayload = (data: any) => {
    const payload = { ...data };

    if (payload.parameter_format === 'POSITIONAL') {
      delete payload.header.example.header_text_named_params;
      delete payload.body.example.body_text_named_params;
      if (
        payload.header.format === 'TEXT' &&
        Array.isArray(payload.header.example?.header_text) &&
        payload.header.example.header_text.every((item: any) => item.trim() === '')
      ) {
        delete payload.header.example;
      }

      if (
        !payload.body.example?.body_text ||
        payload.body.example.body_text.every((item: any) => item.trim?.() === '')
      ) {
        delete payload.body.example?.body_text;
      }

      if (
        !payload.header.example?.header_text ||
        payload.header.example.header_text.every((item: any) => item.trim?.() === '')
      ) {
        delete payload.header.example?.header_text;
      }

      if (payload.body?.example && Object.keys(payload.body.example).length === 0) {
        delete payload.body.example;
      }
      if (payload.header?.example && Object.keys(payload.header.example).length === 0) {
        delete payload.header.example;
      }
    }

    if (payload.parameter_format === 'NAMED') {
      delete payload.header?.example?.header_text;
      delete payload.body?.example?.body_text;

      const headerNamedParams = payload.header.example?.header_text_named_params || [];
      const hasHeaderNamed = headerNamedParams.some(
        (param: any) => param?.param_name?.trim() !== '' && param?.example?.trim() !== ''
      );
      if (!hasHeaderNamed) {
        delete payload.header.example?.header_text_named_params;
      }

      const bodyNamedParams = payload.body.example?.body_text_named_params || [];
      const hasBodyNamed = bodyNamedParams.some(
        (param: any) => param?.param_name?.trim() !== '' && param?.example?.trim() !== ''
      );
      if (!hasBodyNamed) {
        delete payload.body.example?.body_text_named_params;
      }

      if (payload.header?.example && Object.keys(payload.header.example).length === 0) {
        delete payload.header.example;
      }
      if (payload.body?.example && Object.keys(payload.body.example).length === 0) {
        delete payload.body.example;
      }
    }

    if (payload.header) {
      const cleanedHeader = cleanHeader(payload.header);
      if (cleanedHeader.format === 'none') {
        delete payload.header;
      } else if (cleanedHeader) {
        payload.header = cleanedHeader;
      } else {
        delete payload.header;
      }
    }

    if (payload.body) {
      delete payload.body.error;
    }

    if (payload.body?.example) {
      if (typeof payload.body.example.body_text === 'string') {
        payload.body.example.body_text = [[payload.body.example.body_text.trim()]];
      } else if (Array.isArray(payload.body.example.body_text)) {
        if (
          payload.body.example.body_text.length > 0 &&
          typeof payload.body.example.body_text[0] === 'string'
        ) {
          payload.body.example.body_text = [
            payload.body.example.body_text
              .map((val: any) => val?.toString()?.trim())
              .filter((val: string) => val)
          ];
        } else if (
          payload.body.example.body_text.length > 0 &&
          Array.isArray(payload.body.example.body_text[0])
        ) {
          payload.body.example.body_text = payload.body.example.body_text.map((subarr: any) =>
            Array.isArray(subarr)
              ? subarr.map((val: any) => val?.toString()?.trim()).filter((val: string) => val)
              : subarr
          );
        }
      }

      const bodyTextEmpty =
        !payload.body.example.body_text ||
        payload.body.example.body_text.length === 0 ||
        (Array.isArray(payload.body.example.body_text[0]) &&
          payload.body.example.body_text[0].length === 0);

      const namedParamsEmpty =
        !payload.body.example.body_text_named_params ||
        payload.body.example.body_text_named_params.every(
          (param: any) =>
            (!param.param_name || param.param_name.trim() === '') &&
            (!param.example || param.example.trim() === '')
        );

      if (bodyTextEmpty && namedParamsEmpty) {
        delete payload.body.example;
      }
    }

    if (!payload.footer || !payload.footer.text || payload.footer.text.trim() === '') {
      delete payload.footer;
    }

    if (payload.buttons && payload.buttons.buttons && payload.buttons.buttons.length > 0) {
      payload.buttons.buttons = payload.buttons.buttons.map((btn: any) => {
        const cleanedBtn: any = { type: btn.type };

        if (btn.type === 'QUICK_REPLY' || btn.type === 'QUICK_REPLY_DISABLE_MKT') {
          cleanedBtn.text = btn.text;
        } else if (btn.type === 'URL') {
          cleanedBtn.text = btn.text;
          if (btn.url && btn.url.trim() !== '') {
            cleanedBtn.url = btn.url;

            const isDynamic = cleanedBtn.url.includes('{{1}}');
            if (isDynamic) {
              const exampleValue = btn.example?.[0]?.trim();
              if (exampleValue) {
                cleanedBtn.example = [exampleValue];
              }
            }
          }

          if (btn.example && btn.example.length > 0) {
            cleanedBtn.example = btn.example;
          }
        } else if (btn.type === 'PHONE_NUMBER') {
          cleanedBtn.text = btn.text;
          if (btn.phone_number && btn.phone_number.trim() !== '') {
            const country = btn.country_code?.trim() || '';
            const number = btn.phone_number.trim();
            cleanedBtn.phone_number = `${country}${number}`;
          }
        } else if (btn.type === 'COPY_CODE') {
          if (btn.example && btn.example.trim() !== '') {
            cleanedBtn.example = btn.example;
          }
        }

        return cleanedBtn;
      });

      if (payload.buttons.buttons.length === 0) {
        delete payload.buttons;
      }
    } else {
      delete payload.buttons;
    }

    return payload;
  };

  const transformPayload = (data: Payload): TransformedPayload => {
    const transformed: TransformedPayload = {
      account: data.account,
      waba_account: wabaId,
      template: {
        name: data.name,
        category: data.category,
        parameter_format: data.parameter_format,
        language: data.language,
        components: []
      }
    };

    if (data.header) {
      transformed.template.components.push({ ...data.header });
    }
    if (data.body) {
      transformed.template.components.push({ ...data.body });
    }
    if (data.footer && data.footer.text && data.footer.text.trim() !== '') {
      transformed.template.components.push({ ...data.footer });
    }
    if (data.buttons && (data.buttons as any).buttons && (data.buttons as any).buttons.length > 0) {
      transformed.template.components.push({ ...data.buttons });
    }

    return transformed;
  };

  const submit = async (data: any) => {
    try {
      if (!wabaId) {
        AlertService.toastError(t('templates.wa.error.no-waba-account'));
        navigate('/templates-panel/templates-wa', { replace: true });
        return;
      }

      if (
        data.header &&
        ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(data.header.format) &&
        !data.header.file
      ) {
        AlertService.toastError(t('templates.wa.error.file-required'));
        return;
      }
      if (data.header?.file && wabaId) {
        const formData = new FormData();
        formData.append('waba_account', wabaId);
        formData.append('file', data.header.file);

        const uploadPromise = TemplatesService.uploadMediaTemplateWa(formData);

        await AlertService.toastLoading(
          uploadPromise,
          t('templates.wa.uploading-file-1'),
          t('templates.wa.uploading-file-2'),
          t('templates.wa.uploading-file-3')
        );

        const response = await uploadPromise;

        const mediaId = response?.id;

        if (mediaId) {
          data.header.example = {
            header_handle: [mediaId]
          };
          delete data.header.file;
        }
      }
      const cleanedData: Payload = cleanPayload(data);
      const payload: TransformedPayload = transformPayload(cleanedData);
      const savePromise = TemplatesService.addTemplateWa(payload);

      AlertService.toastLoading(
        savePromise,
        t('templates.wa.loading-save'),
        t('templates.wa.success-save'),
        t('templates.wa.error-save')
      );
      await savePromise;

      navigate('/templates-panel/templates-wa', {
        state: { wabaId, accountName }
      });
    } catch (err: any) {
      console.log('Error: ', err);
      AlertService.toastError(err?.status?.error_user_msg || err.error || 'Error');
    }
  };

  const handleName = ({ target: { value } }: any) => {
    const newValue = value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w\s]/gi, '')
      .replace(/\s+/g, '_')
      .toLowerCase();
    setValue('name', newValue);
  };

  const cleanMalformedVariables = (text: string): string => {
    return text.replace(/{{\s*([^{}]*?)\s*}}/g, (_, content) => {
      return `{{${content.trim()}}}`;
    });
  };

  const validateVariableFormat = (value: string, format: 'POSITIONAL' | 'NAMED'): string | true => {
    if (!value) return true;

    const allVariables = value.match(/{{\s*([^{}]*?)\s*}}/g) || [];

    if (allVariables.length === 0) return true;

    const variableContents = allVariables.map((v) => v.replace(/{{\s*|\s*}}/g, '').trim());

    const hasNumbers = variableContents.some((v) => /^\d+$/.test(v));
    const hasNames = variableContents.some((v) => /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(v));
    const hasEmpty = variableContents.some((v) => v === '');
    const hasInvalid = variableContents.some(
      (v) => v !== '' && !/^\d+$/.test(v) && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(v)
    );

    if (format === 'POSITIONAL') {
      if (hasNames)
        return 'En modo POSITIONAL no se permiten variables con letras. Usa {{1}}, {{2}}, etc.';
      if (hasEmpty) return 'No se permiten variables vacías {{}}';
      if (hasInvalid) return 'Las variables deben contener solo números en modo POSITIONAL';

      const nums = variableContents.filter((v) => /^\d+$/.test(v)).map((v) => parseInt(v, 10));
      if (nums.length) {
        const unique = Array.from(new Set(nums)).sort((a, b) => a - b);
        const max = unique[unique.length - 1];
        if (max !== unique.length) {
          return 'Las variables numéricas deben ser consecutivas empezando desde {{1}}';
        }
      }
    } else if (format === 'NAMED') {
      if (hasNumbers) {
        return 'En modo NAMED no se permiten variables numéricas. Usa {{nombre}} en su lugar.';
      }
      if (hasEmpty) {
        return 'No se permiten variables vacías {{}}';
      }
      if (hasInvalid) {
        return 'Las variables deben empezar con letra y contener solo letras, números y guiones bajos';
      }
    }

    return true;
  };

  useEffect(() => {
    const headerText = watch('header.text');
    const bodyText = watch('body.text');

    if (!headerText && !bodyText) return;

    const convertVariables = (text: string, fromFormat: string, toFormat: string): string => {
      if (!text) return text;

      if (fromFormat === 'POSITIONAL' && toFormat === 'NAMED') {
        return text.replace(/{{\s*(\d+)\s*}}/g, (match, num) => {
          return `{{variable${num}}}`;
        });
      } else if (fromFormat === 'NAMED' && toFormat === 'POSITIONAL') {
        let counter = 1;
        const seenVars = new Map<string, number>();

        return text.replace(/{{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*}}/g, (match, varName) => {
          if (!seenVars.has(varName)) {
            seenVars.set(varName, counter);
            counter++;
          }
          return `{{${seenVars.get(varName)}}}`;
        });
      }

      return text;
    };

    const detectFormat = (text: string): 'POSITIONAL' | 'NAMED' | 'NONE' => {
      if (/{{\s*\d+\s*}}/.test(text)) return 'POSITIONAL';
      if (/{{\s*[a-zA-Z_][a-zA-Z0-9_]*\s*}}/.test(text)) return 'NAMED';
      return 'NONE';
    };

    const headerFormat = detectFormat(headerText || '');
    const bodyFormat = detectFormat(bodyText || '');

    if (headerFormat !== 'NONE' && headerFormat !== parameterFormat) {
      const converted = convertVariables(headerText, headerFormat, parameterFormat);
      setValue('header.text', converted);
    }

    if (bodyFormat !== 'NONE' && bodyFormat !== parameterFormat) {
      const converted = convertVariables(bodyText, bodyFormat, parameterFormat);
      setValue('body.text', converted);
    }

    setTimeout(() => {
      if (headerText) trigger('header.text');
      if (bodyText) trigger('body.text');
    }, 0);
  }, [parameterFormat, trigger, setValue]);

  const handleHeaderInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    value = cleanMalformedVariables(value);

    const allVariables = value.match(/{{\s*([^{}]*?)\s*}}/g) || [];
    const variableContents = allVariables.map((v) => v.replace(/{{\s*|\s*}}/g, '').trim());

    const hasNumbers = variableContents.some((v) => /^\d+$/.test(v));
    const hasNames = variableContents.some((v) => /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(v));

    if (parameterFormat === 'POSITIONAL') {
      if (hasNames) {
        value = value.replace(/{{\s*[a-zA-Z_][a-zA-Z0-9_]*\s*}}/g, '');
      }

      const numberVariables = value.match(/{{\s*\d+\s*}}/g) || [];
      if (numberVariables.length > 0) {
        let replaced = false;
        value = value.replace(/{{\s*\d+\s*}}/g, (match) => {
          if (!replaced) {
            replaced = true;
            return '{{1}}';
          }
          return '';
        });
      }

      if (numberVariables.length >= 1 && value.endsWith('{{')) {
        value = value.slice(0, -2);
      }
    } else if (parameterFormat === 'NAMED') {
      if (hasNumbers) {
        value = value.replace(/{{\s*\d+\s*}}/g, '');
      }

      value = value.replace(/{{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*}}/g, (match, p1) => `{{${p1}}}`);

      const namedVariables = value.match(/{{\s*[a-zA-Z_][a-zA-Z0-9_]*\s*}}/g) || [];

      if (namedVariables.length > 1) {
        let kept = false;
        value = value.replace(/{{\s*[a-zA-Z_][a-zA-Z0-9_]*\s*}}/g, (match) => {
          if (!kept) {
            kept = true;
            return match;
          }
          return '';
        });
      }

      if (namedVariables.length >= 1 && value.endsWith('{{')) {
        value = value.slice(0, -2);
      }
    }

    setValue('header.text', value);
  };

  const handleBodyInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let value = e.target.value;

    value = cleanMalformedVariables(value);

    const allVariables = value.match(/{{\s*([^{}]*?)\s*}}/g) || [];
    const variableContents = allVariables.map((v) => v.replace(/{{\s*|\s*}}/g, '').trim());

    const hasNumbers = variableContents.some((v) => /^\d+$/.test(v));
    const hasNames = variableContents.some((v) => /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(v));

    if (parameterFormat === 'POSITIONAL') {
      if (hasNames) {
        value = value.replace(/{{\s*[a-zA-Z_][a-zA-Z0-9_]*\s*}}/g, '');
      }

      if (value.endsWith('{{')) {
        const existingNumbers = (value.match(/{{\s*\d+\s*}}/g) || [])
          .map((v) => parseInt(v.replace(/{{\s*|\s*}}/g, '')))
          .filter((n) => !isNaN(n))
          .sort((a, b) => a - b);

        const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;

        value = value + nextNumber + '}}';
      }

      const numberMatches = value.match(/{{\s*\d+\s*}}/g) || [];
      const numbers = numberMatches
        .map((m) => parseInt(m.replace(/{{\s*|\s*}}/g, '')))
        .filter((n) => !isNaN(n));
    } else if (parameterFormat === 'NAMED') {
      if (hasNumbers) {
        value = value.replace(/{{\s*\d+\s*}}/g, '');
      }
      value = value.replace(/{{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*}}/g, (match, p1) => `{{${p1}}}`);
    }

    setValue('body.text', value);
  };

  const watchedButtons = useWatch({
    control,
    name: 'buttons.buttons'
  });

  const urlButtonsCount = watchedButtons?.filter((btn: any) => btn.type === 'URL').length || 0;
  const offerCodeButtonsCount =
    watchedButtons?.filter((btn: any) => btn.type === 'COPY_CODE').length || 0;
  const phoneNumberButtonsCount =
    watchedButtons?.filter((btn: any) => btn.type === 'PHONE_NUMBER').length || 0;
  const disabledButton =
    watchedButtons?.filter((btn: any) => btn.type === 'QUICK_REPLY_DISABLE_MKT').length || 0;

  const addBtn = (type: string) => {
    if (type === 'URL' && urlButtonsCount >= 2) {
      AlertService.toastWarning(t('templates.wa.warning-url'));
      return;
    }
    if (type === 'COPY_CODE' && offerCodeButtonsCount >= 1) {
      AlertService.toastWarning(t('templates.wa.create.warning-offer-code'));
      return;
    }
    if (type === 'PHONE_NUMBER' && phoneNumberButtonsCount >= 1) {
      AlertService.toastWarning(t('templates.wa.create.warning-phone-number'));
      return;
    }
    if (type === 'QUICK_REPLY_DISABLE_MKT' && disabledButton >= 1) {
      AlertService.toastWarning(t('templates.wa.create.warning-disabled-quick-reply'));
      return;
    }

    if (buttonActionsArray.fields.length < 10) {
      if (type === 'COPY_CODE') {
        buttonActionsArray.append({
          ...defaultButtonValues,
          type,
          text: t('templates.wa.create.copy-offer-code-text')
        });
      } else {
        buttonActionsArray.append({
          ...defaultButtonValues,
          type
        });
      }

      if (buttonActionsArray.fields.length === 3) {
        AlertService.toastWarning(t('templates.wa.create.all-options-warning'));
      }
    } else {
      AlertService.toastWarning(t('templates.wa.warning-buttons'));
    }
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue('category', event.target.value);
  };

  const handleKeyDown = (e: any, maxLength: number) => {
    const currentLength = e.target.value.length;
    if (currentLength >= maxLength && e.key !== 'Backspace' && e.key !== 'Delete') {
      e.preventDefault();
    }
  };

  const updateFiles = (incomingFiles: any) => {
    const file = incomingFiles[0]?.file as any;

    if (!file) return;

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const mimeType = file.type;
    const sizeMB = +(file.size / (1024 * 1024)).toFixed(2);

    const validFormats: Record<
      'IMAGE' | 'VIDEO' | 'DOCUMENT',
      {
        extensions: string[];
        mimeTypes: string[];
      }
    > = {
      IMAGE: {
        extensions: ['jpeg', 'jpg', 'png'],
        mimeTypes: ['image/jpeg', 'image/png']
      },
      VIDEO: {
        extensions: ['mp4'],
        mimeTypes: ['video/mp4']
      },
      DOCUMENT: {
        extensions: ['pdf'],
        mimeTypes: ['application/pdf']
      }
    };

    const currentFormat = header.format as 'IMAGE' | 'VIDEO' | 'DOCUMENT';

    const isValidFormat =
      validFormats[currentFormat]?.extensions.includes(fileExtension) &&
      validFormats[currentFormat]?.mimeTypes.includes(mimeType);

    if (sizeMB > 5) {
      AlertService.toastError(t('templates.wa.create.error-size'));
      setValue('header.file', '');
      return;
    }

    if (!isValidFormat) {
      AlertService.toastError(t('templates.wa.create.file-error'));
      setValue('header.file', '');
      return;
    }

    setValue('header.file', file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImageSrc(reader.result as any);
    };
    reader.readAsDataURL(file);
  };

  const removeFile = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    setValue('header.file', '');
    setImageSrc(null);
  };

  const totalButtons = buttons?.buttons?.length || 0;
  const visibleButtons = totalButtons > 3 ? buttons.buttons.slice(0, 2) : buttons.buttons;

  const getCompanies = async () => {
    try {
      const data = await CompaniesService.getCompanies();
      setCompanies(
        data.map((item: any) => ({
          value: item._id,
          label: item.name,
          type: item.service_type
        }))
      );
    } catch (errorCode: any) {
      console.error('Error obteniendo empresas:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
      setCompanies([]);
    }
  };

  const getAccount = async () => {
    if (selectedCompany) {
      try {
        const data = await AccountsService.getAccountsForCompany(selectedCompany);
        setAccountOptions(
          data.map((item: any) => ({
            value: item._id,
            label: item.name,
            type: item.service_type
          }))
        );
      } catch (errorCode: any) {
        AlertService.toastError(errorCode.error || errorCode);
        console.error('Error obteniendo empresas:', errorCode);
        setAccountOptions([]);
      }
    }
  };

  // Funciones para formatear texto de WhatsApp

  function parseBold(text: string): string {
    return text.replace(/\*(.*?)\*/g, '<strong>$1</strong>');
  }

  function parseStrikethrough(text: string): string {
    return text.replace(/~(.*?)~/g, '<del>$1</del>');
  }

  function parseWhatsAppFormatting(text: string): string {
    let formatted = text;
    formatted = parseBold(formatted);
    formatted = parseStrikethrough(formatted);
    formatted = formatted.replace(/\n/g, '<br/>');
    return formatted;
  }

  useEffect(() => {
    if (!wabaId) return;
    getCompanies();
  }, []);

  useEffect(() => {
    getAccount();
  }, [selectedCompany]);

  useEffect(() => {
    if (session.profile === PROFILES.ADMIN) {
      setValue('account', selectedAccount.account_id);
    }
  }, [selectedAccount, session]);

  useEffect(() => {
    if (!wabaId) {
      AlertService.toastError(t('templates.wa.error.no-waba-account'));
      navigate('/templates-panel/accounts-wa', { replace: true });
      return;
    }
  }, [wabaId, navigate, t]);

  return (
    <>
      <div className="page d-flex flex-row flex-column-fluid">
        <Aside />
        <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
          <Header />
          <div className="content fs-6 d-flex flex-column flex-column-fluid" id="kt_content">
            <div className="post fs-6 d-flex flex-column-fluid mt-5" id="kt_post">
              <div className="container-xxl">
                <Navigate
                  btnBack
                  title={t('templates.wa.create.create-title')}
                  titleBolder={t('templates.wa.wa-title')}
                  subtitle={
                    t('templates.wa.subtitle') ||
                    'Personaliza y gestiona tus plantillas de WhatsApp'
                  }
                  breadcrumbItems={[
                    'templates.wa.tools',
                    'templates.templates-title',
                    'templates.wa.wa-account',
                    'templates.wa.create-template'
                  ]}
                />
                <form className="form" onSubmit={handleSubmit(submit)}>
                  <div className="row g-xl-10">
                    <div className="col-xl-8 mb-5">
                      <div className="card mb-5 mb-xxl-8">
                        <div className="card-header align-items-center border-0 mt-10">
                          <Title
                            icon="ki-messages"
                            title={t('templates.wa.create.type-title')}
                            subtitle={t('templates.wa.create.type-description')}
                            type="primary"
                            required
                          />
                        </div>
                        <div className="card-body pt-1">
                          <div className="row mb-5">
                            <div className="col-md-12 mb-5"></div>
                            <div className="fv-row">
                              <label
                                className={`btn btn-outline btn-outline-dashed btn-active-light-primary d-flex text-start p-6 mb-6 ${
                                  watch('category') === 'MARKETING' ? 'active' : ''
                                }`}
                              >
                                <input
                                  className="btn-check"
                                  type="radio"
                                  name="project_type"
                                  checked={watch('category') === 'MARKETING'}
                                  value="MARKETING"
                                  onChange={handleRadioChange}
                                />
                                <span className="d-flex align-items-center">
                                  <Icon icon="ki-price-tag" size="fs-3x" />
                                  <span className="ms-4">
                                    <span className="fs-3 fw-bold text-gray-900 mb-2 d-block">
                                      {t('templates.wa.marketing-name')}
                                    </span>
                                    <span className="fw-semibold fs-4 text-muted">
                                      {t('templates.wa.create.marketing-description')}
                                    </span>
                                  </span>
                                </span>
                              </label>
                              {/* <label
                                className={`btn btn-outline btn-outline-dashed btn-active-light-primary d-flex text-start p-6 mb-6 ${
                                  watch('category') === 'UTILITY' ? 'active' : ''
                                }`}
                              >
                                <input
                                  className="btn-check"
                                  type="radio"
                                  checked={watch('category') === 'UTILITY'}
                                  name="project_type"
                                  value="UTILITY"
                                  onChange={handleRadioChange}
                                />
                                <span className="d-flex align-items-center">
                                  <Icon icon="ki-notification" size="fs-3x" />
                                  <span className="ms-4">
                                    <span className="fs-3 fw-bold text-gray-900 mb-2 d-block">
                                      {t('templates.wa.utility-name')}
                                    </span>

                                    <span className="fw-semibold fs-4 text-muted">
                                      {t('templates.wa.create.utility-description')}
                                    </span>
                                  </span>
                                </span>
                              </label>
                              <label
                                className={`btn btn-outline btn-outline-dashed btn-active-light-primary d-flex text-start p-6 mb-6 ${
                                  watch('category') === 'AUTHENTICATION' ? 'active' : ''
                                }`}
                              >
                                <input
                                  className="btn-check"
                                  type="radio"
                                  name="project_type"
                                  checked={watch('category') === 'AUTHENTICATION'}
                                  value="AUTHENTICATION"
                                  onChange={handleRadioChange}
                                />
                                <span className="d-flex align-items-center">
                                  <Icon icon="ki-key" size="fs-3x" />
                                  <span className="ms-4">
                                    <span className="fs-3 fw-bold text-gray-900 mb-2 d-block">
                                      {t('templates.wa.authentication-name')}
                                    </span>

                                    <span className="fw-semibold fs-4 text-muted">
                                      {t('templates.wa.create.authentication-description')}
                                    </span>
                                  </span>
                                </span>
                              </label> */}
                            </div>
                          </div>
                        </div>
                      </div>
                      {watch('category') !== '' && (
                        <>
                          <div className="card mb-5 mb-xxl-8">
                            <div className="card-header align-items-center border-0 mt-10">
                              <Title
                                icon="ki-note-2"
                                title={t('templates.wa.create.data-title')}
                                subtitle={t('templates.wa.create.data-description')}
                                type="primary"
                                required
                              />
                            </div>
                            <div className="card-body pt-1">
                              <div className="row">
                                {session.profile === PROFILES.ROOT && (
                                  <>
                                    <div className="col-6">
                                      <div className="fv-row mb-7">
                                        <label className="form-label">
                                          {t('blacklist-page.company-label')}
                                          <span className="fs-8 text-muted ms-1 required"></span>
                                        </label>
                                        <Controller
                                          control={control}
                                          name="companies"
                                          rules={{ required: true }}
                                          render={({
                                            field: { onChange, value },
                                            fieldState: { error }
                                          }) => (
                                            <>
                                              <Select
                                                options={companies}
                                                noOptionsMessage={({ inputValue }) =>
                                                  inputValue
                                                    ? t('general.no-options', { inputValue })
                                                    : t('general.no-options')
                                                }
                                                className={
                                                  error
                                                    ? 'form-select-custom is-invalid'
                                                    : 'form-select-custom'
                                                }
                                                classNamePrefix="form-select-custom"
                                                placeholder={t(
                                                  'blacklist-page.company-placeholder'
                                                )}
                                                onChange={(selectedOption) => {
                                                  const selectedValue = selectedOption
                                                    ? selectedOption.value
                                                    : '';
                                                  setSelectedCompany(selectedValue);
                                                  onChange(selectedValue);
                                                }}
                                                value={
                                                  companies.filter((option: any) =>
                                                    value?.includes(option.value)
                                                  ) || []
                                                }
                                              />
                                              <InvalidFeedback error={error} />
                                            </>
                                          )}
                                        />
                                      </div>
                                    </div>
                                    <div className="col-6">
                                      <div className="fv-row mb-7">
                                        <label className="form-label required">
                                          {t('blacklist-page.account-label')}
                                        </label>
                                        <Controller
                                          control={control}
                                          name="account"
                                          rules={{ required: true }}
                                          render={({
                                            field: { onChange, value },
                                            fieldState: { error }
                                          }) => (
                                            <>
                                              <Select
                                                options={accountOptions}
                                                isClearable
                                                noOptionsMessage={({ inputValue }) =>
                                                  inputValue
                                                    ? t('general.no-options', { inputValue })
                                                    : t('general.no-options')
                                                }
                                                className={
                                                  error
                                                    ? 'form-select-custom is-invalid'
                                                    : 'form-select-custom'
                                                }
                                                classNamePrefix="form-select-custom"
                                                placeholder={t(
                                                  'blacklist-page.account-placeholder'
                                                )}
                                                onChange={(selectedOption) => {
                                                  onChange(selectedOption?.value);
                                                }}
                                                value={
                                                  accountOptions.find(
                                                    (option: any) => option.value === value
                                                  ) || null
                                                }
                                              />
                                              <InvalidFeedback error={error} />
                                            </>
                                          )}
                                        />
                                      </div>
                                    </div>
                                  </>
                                )}
                                <div className="col-8">
                                  <Input
                                    label={t('templates.wa.create.name-label')}
                                    currentLength={watch('name')?.length}
                                    maxLength={512}
                                    requiredIndicator="required"
                                    placeholder={t('templates.wa.create.name-placeholder')}
                                    errors={errors.name}
                                    onKeyDown={(e: any) => handleKeyDown(e, 512)}
                                    {...register('name', {
                                      required: true,
                                      onChange: handleName,
                                      maxLength: { value: 512, message: '512' }
                                    })}
                                  />
                                </div>
                                <div className="col-4">
                                  <label className="form-label required">
                                    {t('templates.wa.create.language-label')}
                                  </label>
                                  <Controller
                                    control={control}
                                    name="language"
                                    rules={{ required: true }}
                                    render={({
                                      field: { onChange, value },
                                      fieldState: { error }
                                    }) => (
                                      <>
                                        <Select
                                          options={languageOptions}
                                          noOptionsMessage={({ inputValue }) =>
                                            inputValue
                                              ? t('general.no-options', { inputValue })
                                              : t('general.no-options')
                                          }
                                          className={
                                            error
                                              ? 'form-select-custom is-invalid'
                                              : 'form-select-custom'
                                          }
                                          classNamePrefix="form-select-custom"
                                          placeholder={t(
                                            'templates.wa.create.language-placeholder'
                                          )}
                                          onChange={(
                                            selectedOption: { value: string; label: string } | null
                                          ) => {
                                            onChange(selectedOption ? selectedOption.value : null);
                                            setValue(
                                              'language',
                                              selectedOption ? selectedOption.value : ''
                                            );
                                          }}
                                          value={
                                            languageOptions.find(
                                              (language: any) => language.value === value
                                            ) || null
                                          }
                                        />
                                        <InvalidFeedback error={error} />
                                      </>
                                    )}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="card mb-5 mb-xxl-8">
                            <div className="card-header align-items-center justify-content-between border-0 mt-10 d-flex">
                              <div className="col-8 mw-400px">
                                <Title
                                  icon="ki-burger-menu-6"
                                  title={t('templates.wa.create.content-title')}
                                  subtitle={t('templates.wa.create.content-description')}
                                  type="primary"
                                  required
                                />
                              </div>
                              <div className="col-4">
                                <label className="form-label required">
                                  {t('templates.wa.create.variable-format-label')}
                                </label>
                                <Tooltip
                                  title={
                                    'Las variables se utilizan para insertar información en tu mensaje. Puedes utilizar variables de tipo numero o nombre.'
                                  }
                                  className="width-fit-content d-inline-block cursor-pointer"
                                  placement="top"
                                >
                                  <Icon icon="ki-information-3" className="ms-2" type="info" />
                                </Tooltip>
                                <Controller
                                  control={control}
                                  name="parameter_format"
                                  rules={{ required: true }}
                                  render={({
                                    field: { onChange, value },
                                    fieldState: { error }
                                  }) => (
                                    <>
                                      <Select
                                        options={parameterFormatOptions}
                                        noOptionsMessage={({ inputValue }) =>
                                          inputValue
                                            ? t('general.no-options', { inputValue })
                                            : t('general.no-options')
                                        }
                                        className={
                                          error
                                            ? 'form-select-custom is-invalid'
                                            : 'form-select-custom'
                                        }
                                        classNamePrefix="form-select-custom"
                                        placeholder={t(
                                          'templates.wa.create.variable-format-placeholder'
                                        )}
                                        onChange={(
                                          selectedOption: { value: string; label: string } | null
                                        ) => {
                                          onChange(selectedOption ? selectedOption.value : null);
                                          setValue(
                                            'parameter_format',
                                            selectedOption ? selectedOption.value : ''
                                          );
                                        }}
                                        value={
                                          parameterFormatOptions.find(
                                            (option) => option.value === value
                                          ) || null
                                        }
                                      />
                                      <InvalidFeedback error={error} />
                                    </>
                                  )}
                                />
                              </div>
                            </div>
                            <div className="card-body pt-1">
                              <div className="col">
                                <div className="alert alert-dismissible bg-light-primary border border-primary d-flex border-dashed flex-column flex-sm-row p-5 mb-10 animate-fadeInUp animate-delay-1">
                                  <Icon icon="ki-information" size="fs-2hx" type="primary" />
                                  <div className="d-flex flex-column pe-0 pe-sm-10 ms-5">
                                    <h5 className="mb-1 text-primary fw-bolder fs-3 mb-3">
                                      {t('general.important')}
                                    </h5>
                                    <span>{t('templates.wa.create.variables-disclaimer')}</span>
                                  </div>
                                </div>
                                <label className="card-title h4 mb-3">
                                  {t('templates.wa.create.header-label')}{' '}
                                  <span className="fs-8 text-muted ms-1">
                                    {t('templates.wa.create.optional')}
                                  </span>
                                </label>
                                <Controller
                                  control={control}
                                  name="header.format"
                                  rules={{ required: true }}
                                  render={({
                                    field: { onChange, value },
                                    fieldState: { error }
                                  }) => (
                                    <>
                                      <Select
                                        options={headerOptions}
                                        noOptionsMessage={({ inputValue }) =>
                                          inputValue
                                            ? t('general.no-options', { inputValue })
                                            : t('general.no-options')
                                        }
                                        className={
                                          error
                                            ? 'form-select-custom is-invalid'
                                            : 'form-select-custom'
                                        }
                                        classNamePrefix="form-select-custom"
                                        placeholder={t('templates.wa.create.header-placeholder')}
                                        onChange={(
                                          selectedOption: { value: string; label: string } | null
                                        ) => {
                                          onChange(selectedOption ? selectedOption.value : null);
                                          {
                                            header && resetField('header.text'),
                                              resetField('header.file'),
                                              setImageSrc(null);
                                          }
                                          setValue(
                                            'header.format',
                                            selectedOption ? selectedOption.value : ''
                                          );
                                        }}
                                        value={
                                          headerOptions.find(
                                            (header: any) => header.value === value
                                          ) || null
                                        }
                                      />
                                      <InvalidFeedback error={error} />
                                    </>
                                  )}
                                />
                              </div>
                              {header.format === 'TEXT' && (
                                <>
                                  <div className="col mt-5">
                                    <div className="fv-row">
                                      <Input
                                        label={t('templates.wa.create.header-option-2')}
                                        currentLength={watch('header.text')?.length}
                                        maxLength={60}
                                        placeholder={t('templates.wa.create.text-placeholder')}
                                        errors={errors.header?.text}
                                        onKeyDown={(e: any) => handleKeyDown(e, 60)}
                                        {...register(`header.text`, {
                                          required: true,
                                          maxLength: { value: 60, message: '60' },
                                          onChange: handleHeaderInputChange,
                                          validate: (value) =>
                                            validateVariableFormat(value, parameterFormat)
                                        })}
                                      />
                                    </div>
                                  </div>
                                  {showVariables && (
                                    <div className="mb-3">
                                      <div
                                        className={`${isDarkTheme ? style.bg_alert_dark : style.bg_alert}`}
                                        role="alert"
                                      >
                                        <h5>
                                          {t('templates.wa.create.alert-header-variables-title')}
                                        </h5>
                                        <p>{t('templates.wa.create.alert-body-variables-text')}</p>
                                        <h6>{t('templates.wa.create.body-label-1')}</h6>
                                        {headerVars.fields.map((row, index) => {
                                          const paramLabel =
                                            parameterFormat === 'POSITIONAL'
                                              ? `{{${index + 1}}}`
                                              : `{{${headerNamedParams?.[index]?.param_name || ''}}}`;
                                          return (
                                            <div
                                              className="d-flex align-items-center mb-2"
                                              key={row.id}
                                            >
                                              <label className="form-label mx-3">
                                                {paramLabel}
                                              </label>
                                              <div className="flex-grow-1">
                                                {parameterFormat === 'POSITIONAL' ? (
                                                  <Input
                                                    errors={
                                                      errors.header?.example?.header_text?.[index]
                                                    }
                                                    {...register(
                                                      `header.example.header_text.${index}` as never,
                                                      { required: true }
                                                    )}
                                                    sizeClass={`form-control-sm ${isDarkTheme ? 'bg-dark' : 'bg-white'}`}
                                                  />
                                                ) : (
                                                  <Input
                                                    errors={
                                                      errors.header?.example
                                                        ?.header_text_named_params?.[index]
                                                    }
                                                    {...register(
                                                      `header.example.header_text_named_params.${index}.example` as never,
                                                      { required: true }
                                                    )}
                                                    sizeClass="form-control-sm"
                                                  />
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                              {(header.format === 'IMAGE' ||
                                header.format === 'VIDEO' ||
                                header.format === 'DOCUMENT') && (
                                <div className="col-12 mt-3">
                                  <div>
                                    <div className="alert alert-soft-secondary" role="alert">
                                      <h5>{t('templates.wa.create.alert-media-title')}</h5>
                                      <p>{t('templates.wa.create.alert-media-text')}</p>
                                      <div>
                                        {!header.file ? (
                                          <Dropzone
                                            onChange={(incomingFiles) => updateFiles(incomingFiles)}
                                            className="dropzone cursor-default"
                                            accept={
                                              header.format === 'IMAGE'
                                                ? 'image/jpeg,image/png'
                                                : header.format === 'VIDEO'
                                                  ? 'video/mp4'
                                                  : header.format === 'DOCUMENT'
                                                    ? 'application/pdf'
                                                    : ''
                                            }
                                            maxFiles={1}
                                          >
                                            <div className="dz-mkssage">
                                              <i className="ki-duotone ki-file-up fs-5x text-primary me-4">
                                                <span className="path1" />
                                                <span className="path2" />
                                              </i>
                                              <div className="d-flex flex-center flex-grow-1 flex-wrap flex-md-nowrap">
                                                <div className="mb-3 mb-md-0 fw-semibold">
                                                  <h5 className="text-gray-900 fw-bold">
                                                    {header.format === 'IMAGE'
                                                      ? t('templates.wa.create.upload-image')
                                                      : header.format === 'VIDEO'
                                                        ? t('templates.wa.create.upload-video')
                                                        : t('templates.wa.create.upload-document')}
                                                  </h5>
                                                  <div className="fs-7 pe-7 text-info">
                                                    {header.format === 'IMAGE'
                                                      ? t(
                                                          'templates.wa.create.upload-image-description'
                                                        )
                                                      : header.format === 'VIDEO'
                                                        ? t(
                                                            'templates.wa.create.upload-video-description'
                                                          )
                                                        : t(
                                                            'templates.wa.create.upload-document-description'
                                                          )}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          </Dropzone>
                                        ) : (
                                          <div className="dropzone cursor-default">
                                            <div className="dz-g">
                                              <i className="ki-duotone ki-file-added text-primary fs-5x text-primary me-4">
                                                <span className="path1" />
                                                <span className="path2" />
                                              </i>
                                              <div className="d-flex flex-column align-items-center flex-grow-1">
                                                <div className="mb-3 mb-md-0 fw-semibold text-center">
                                                  <h5 className="text-gray-900 fw-bold">
                                                    {t('templates.wa.create.file-uploaded')}
                                                  </h5>
                                                  <div className="fs-7 text-info">
                                                    {typeof header.file === 'object' &&
                                                    header.file !== null &&
                                                    'name' in header.file
                                                      ? (header.file as File).name
                                                      : t('templates.wa.create.no-file-uploaded')}
                                                  </div>
                                                </div>
                                                <a
                                                  className="btn btn-danger px-6 text-nowrap btn-sm mt-3"
                                                  href="#"
                                                  onClick={removeFile}
                                                >
                                                  {t('general.delete')}
                                                </a>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              <div className="fv-row mt-5">
                                <label className="card-title h4 required">
                                  {t('templates.wa.create.body-label')}
                                </label>
                                <TextArea
                                  placeholder={t('templates.wa.create.body-placeholder')}
                                  errors={errors.body?.text}
                                  value={watch('body.text')?.length}
                                  counter={1024}
                                  onKeyDown={(e: any) => handleKeyDown(e, 1024)}
                                  {...register('body.text', {
                                    required: true,
                                    maxLength: { value: 1024, message: '1024' },
                                    onChange: handleBodyInputChange,
                                    validate: (value) =>
                                      validateVariableFormat(value, parameterFormat)
                                  })}
                                />
                                {bodyShowVariables && (
                                  <div className="mb-3">
                                    <div
                                      className={`${isDarkTheme ? style.bg_alert_dark : style.bg_alert}`}
                                      role="alert"
                                    >
                                      <h5>{t('templates.wa.create.alert-body-variables-title')}</h5>
                                      <p>{t('templates.wa.create.alert-body-variables-text')}</p>

                                      <h6>{t('templates.wa.create.body-label-1')}</h6>
                                      {bodyVars.fields.map((row, index) => {
                                        const paramLabel =
                                          parameterFormat === 'POSITIONAL'
                                            ? `{{${index + 1}}}`
                                            : `{{${bodyNamedParams?.[index]?.param_name || ''}}}`;
                                        return (
                                          <div
                                            className="d-flex align-items-center mb-2"
                                            key={row.id}
                                          >
                                            <label className="form-label mx-3">{paramLabel}</label>
                                            <div className="flex-grow-1">
                                              {parameterFormat === 'POSITIONAL' ? (
                                                <Input
                                                  errors={errors.body?.example?.body_text?.[index]}
                                                  {...register(
                                                    `body.example.body_text.0.${index}` as never,
                                                    { required: true }
                                                  )}
                                                  sizeClass={`form-control-sm ${isDarkTheme ? 'bg-dark' : 'bg-white'}`}
                                                />
                                              ) : (
                                                <Input
                                                  errors={
                                                    errors.body?.example?.body_text_named_params?.[
                                                      index
                                                    ]
                                                  }
                                                  {...register(
                                                    `body.example.body_text_named_params.${index}.example` as never,
                                                    { required: true }
                                                  )}
                                                  sizeClass="form-control-sm"
                                                />
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div>
                                <label className="card-title h4">
                                  {t('templates.wa.create.footer-label')}{' '}
                                  <span className="fs-8 text-muted ms-1">
                                    {t('templates.wa.create.optional')}
                                  </span>
                                </label>
                                <Input
                                  currentLength={watch(`footer.text`).length}
                                  maxLength={60}
                                  placeholder={t('templates.wa.create.footer-placeholder')}
                                  errors={errors.footer?.text}
                                  onKeyDown={(e: any) => handleKeyDown(e, 60)}
                                  {...register(`footer.text`, {
                                    required: false,
                                    maxLength: { value: 60, message: '60' }
                                  })}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="card mb-5 mb-xxl-8">
                            <div className="card-header align-items-center border-0 mt-10">
                              <Title
                                icon="ki-exit-right"
                                title={t('templates.wa.create.buttons-title')}
                                subtitle={t('templates.wa.create.buttons-description')}
                                type="primary"
                                optional
                              />
                            </div>
                            <div className="card-body">
                              <div className="row">
                                <div className=" col-12">
                                  <div className="dropdown">
                                    <button
                                      className="btn btn-secondary btn-sm"
                                      type="button"
                                      id="dropdownMenuButton"
                                      data-bs-toggle="dropdown"
                                      aria-expanded="false"
                                    >
                                      <i className="bi-plus-lg"></i>
                                      {t('templates.wa.create.add-buttons')}
                                    </button>
                                    <div
                                      className="dropdown-menu p-2 cursor-pointer"
                                      aria-labelledby="dropdownMenuButton"
                                    >
                                      <a
                                        className="dropdown-item"
                                        onClick={() => addBtn('QUICK_REPLY')}
                                      >
                                        {t('templates.wa.create.quick-reply')}
                                      </a>
                                      <a
                                        className={`dropdown-item disabled ${disabledButton >= 1 ? 'disabled' : ''}`}
                                        onClick={() => addBtn('QUICK_REPLY_DISABLE_MKT')}
                                      >
                                        {t('templates.wa.create.quick-reply-disable')}
                                      </a>
                                      <a
                                        className={`dropdown-item ${urlButtonsCount >= 2 ? 'disabled' : ''}`}
                                        onClick={() => addBtn('URL')}
                                      >
                                        {t('templates.wa.create.go-url')}
                                      </a>
                                      <a
                                        className={`dropdown-item disabled ${offerCodeButtonsCount >= 1 ? 'disabled' : ''}`}
                                        onClick={() => addBtn('COPY_CODE')}
                                      >
                                        {t('templates.wa.create.copy-offer-code')}
                                      </a>
                                      <a
                                        className={`dropdown-item ${phoneNumberButtonsCount >= 1 ? 'disabled' : ''}`}
                                        onClick={() => addBtn('PHONE_NUMBER')}
                                      >
                                        {t('templates.wa.create.call-phone')}
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {buttonActionsArray.fields.length > 0 && (
                                <div className="row mb-3" key={buttonActionsArray.fields.length}>
                                  <div className="col">
                                    <ButtonsDnd
                                      fields={buttonActionsArray.fields}
                                      move={(from: any, to: any) =>
                                        buttonActionsArray.move(from, to)
                                      }
                                      renderButton={(row: any, index: any) => {
                                        if (
                                          row.type === 'QUICK_REPLY' ||
                                          row.type === 'QUICK_REPLY_DISABLE_MKT'
                                        ) {
                                          return (
                                            <QuickReplyBtn
                                              errors={errors.buttons?.buttons?.[index]}
                                              register={register}
                                              index={index}
                                              watch={watch}
                                              onDelete={() => buttonActionsArray.remove(index)}
                                              type={row.type}
                                            />
                                          );
                                        }
                                        if (row.type === 'URL') {
                                          return (
                                            <UrlBtn
                                              errors={errors.buttons?.buttons?.[index]}
                                              register={register}
                                              index={index}
                                              watch={watch}
                                              control={control}
                                              onDelete={() => buttonActionsArray.remove(index)}
                                            />
                                          );
                                        }
                                        if (row.type === 'PHONE_NUMBER') {
                                          return (
                                            <PhoneNumberBtn
                                              errors={errors.buttons?.buttons?.[index]}
                                              register={register}
                                              index={index}
                                              watch={watch}
                                              onDelete={() => buttonActionsArray.remove(index)}
                                              control={control}
                                            />
                                          );
                                        }
                                        if (row.type === 'COPY_CODE') {
                                          return (
                                            <OfferCodeBtn
                                              errors={errors.buttons?.buttons?.[index]}
                                              register={register}
                                              index={index}
                                              watch={watch}
                                              onDelete={() => buttonActionsArray.remove(index)}
                                            />
                                          );
                                        }
                                        return null;
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="col-xl-4">
                      <div className="sticky-box">
                        <div className={isDarkTheme ? 'wrap-iphone-wa-dark' : 'wrap-iphone-wa'}>
                          <div className={`${style.bg_whatsApp}`}>
                            <div className={`${style.container_message}`}>
                              <div className={`${style.sticky}`}>
                                <div className="mt-3 me-2">
                                  <div
                                    className={`${isDarkTheme ? style.preview_template_dark : style.preview_template}`}
                                  >
                                    <div style={{ padding: '16px 16px 2px 16px' }}>
                                      {header.format === 'TEXT' && (
                                        <div
                                          className={`${isDarkTheme ? style.preview_template_dark_header : style.preview_template_header}`}
                                        >
                                          <strong>{watch('header.text')}</strong>
                                        </div>
                                      )}
                                      {header.format === 'IMAGE' && (
                                        <div
                                          className={`${isDarkTheme ? style.preview_template_dark_header : style.preview_template_header}`}
                                        >
                                          <div
                                            className={`${isDarkTheme ? style.preview_template_dark_image : style.preview_template_image}`}
                                            style={{ backgroundImage: `url(${imageSrc})` }}
                                          >
                                            {!imageSrc && <i className="bi-card-image"></i>}
                                          </div>
                                        </div>
                                      )}
                                      {header.format === 'VIDEO' && (
                                        <div
                                          className={`${isDarkTheme ? style.preview_template_dark_header : style.preview_template_header}`}
                                        >
                                          {' '}
                                          <div
                                            className={`${isDarkTheme ? style.preview_template_dark_video : style.preview_template_video}`}
                                          >
                                            {' '}
                                            <i className="bi-play-circle-fill"></i>
                                          </div>{' '}
                                        </div>
                                      )}
                                      {header.format === 'DOCUMENT' && (
                                        <div
                                          className={`${isDarkTheme ? style.preview_template_dark_header : style.preview_template_header}`}
                                        >
                                          {' '}
                                          <div
                                            className={`${isDarkTheme ? style.preview_template_dark_video : style.preview_template_video}`}
                                          >
                                            {' '}
                                            <i className="bi-filetype-pdf"></i>
                                          </div>{' '}
                                        </div>
                                      )}
                                      {header.format === 'LOCATION' && (
                                        <div
                                          className={`${isDarkTheme ? style.preview_template_dark_header : style.preview_template_header}`}
                                        >
                                          {' '}
                                          <div
                                            className={`${isDarkTheme ? style.preview_template_dark_video : style.preview_template_video}`}
                                          >
                                            {' '}
                                            <i className="bi-geo-alt"></i>
                                          </div>{' '}
                                        </div>
                                      )}
                                      <div
                                        dangerouslySetInnerHTML={{
                                          __html: parseWhatsAppFormatting(watch('body.text') || '')
                                        }}
                                      ></div>
                                      <div
                                        className={`${isDarkTheme ? style.preview_template_dark_footer : style.preview_template_footer}`}
                                      >
                                        {footer}
                                      </div>
                                    </div>
                                    {totalButtons > 0 && (
                                      <>
                                        {visibleButtons.map((btn: any, index: number) => {
                                          return (
                                            <div key={index}>
                                              <hr className="my-0" />
                                              <div
                                                className={
                                                  `${isDarkTheme ? style.preview_template_dark_btn : style.preview_template_btn} ` +
                                                  `${isDarkTheme ? style.preview_template_dark : style.preview_template}`
                                                }
                                                style={{ boxShadow: 'none', margin: '5px 0' }}
                                              >
                                                {btn.type === 'URL' && (
                                                  <>
                                                    <i className="bi-box-arrow-up-right"></i>{' '}
                                                    {btn.text}
                                                  </>
                                                )}
                                                {btn.type === 'QUICK_REPLY' && (
                                                  <>
                                                    <i className="bi-reply"></i> {btn.text}
                                                  </>
                                                )}
                                                {btn.type === 'QUICK_REPLY_DISABLE_MKT' && (
                                                  <>
                                                    <i className="bi-reply"></i> {btn.text}
                                                  </>
                                                )}
                                                {btn.type === 'PHONE_NUMBER' && (
                                                  <>
                                                    <i className="bi-telephone"></i> {btn.text}
                                                  </>
                                                )}
                                                {btn.type === 'COPY_CODE' && (
                                                  <>
                                                    <i className="bi-copy"></i> {btn.text}
                                                  </>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                        {totalButtons > 3 && (
                                          <div
                                            className="cursor-pointer"
                                            onClick={() => setShowAllOptions(true)}
                                          >
                                            <hr className="my-0" />
                                            <div
                                              className={
                                                `${isDarkTheme ? style.preview_template_dark_btn : style.preview_template_btn} ` +
                                                `${isDarkTheme ? style.preview_template_dark : style.preview_template}`
                                              }
                                              style={{ boxShadow: 'none' }}
                                            >
                                              <i className="bi-list me-2"></i>
                                              {t('templates.wa.create.see-all-options')}
                                            </div>
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                            {showAllOptions && totalButtons > 3 && (
                              <div
                                className={
                                  isDarkTheme
                                    ? style.preview_template_dark_options
                                    : style.preview_template_options
                                }
                              >
                                <div className="d-flex align-items-center my-2">
                                  <div onClick={() => setShowAllOptions(false)}>
                                    <i className="bi-x fs-2x ms-3 cursor-pointer"></i>
                                  </div>
                                  <div className="d-flex w-100 text-center justify-content-center align-items-center">
                                    <span className=" me-5 text-center">Todas las opciones</span>
                                  </div>
                                </div>
                                {buttons?.buttons?.map((btn, index) => (
                                  <div key={index}>
                                    <div
                                      className={
                                        (isDarkTheme
                                          ? style.preview_template_dark_btn
                                          : style.preview_template_btn) +
                                        ' text-start ' +
                                        (isDarkTheme
                                          ? style.preview_template_dark
                                          : style.preview_template)
                                      }
                                      style={{ boxShadow: 'none' }}
                                    >
                                      {btn.type === 'URL' && (
                                        <>
                                          <i className="bi-box-arrow-up-right"></i> {btn.text}
                                        </>
                                      )}
                                      {btn.type === 'QUICK_REPLY' && (
                                        <>
                                          <i className="bi-reply"></i> {btn.text}
                                        </>
                                      )}
                                      {btn.type === 'QUICK_REPLY_DISABLE_MKT' && (
                                        <>
                                          <i className="bi-reply"></i> {btn.text}
                                        </>
                                      )}
                                      {btn.type === 'PHONE_NUMBER' && (
                                        <>
                                          <i className="bi-telephone"></i> {btn.text}
                                        </>
                                      )}
                                      {btn.type === 'OFFER_CODE' && (
                                        <>
                                          <i className="bi-copy"></i> {btn.text}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-left pt-2">
                    <Button
                      type="submit"
                      typeStepperBtn="submit"
                      id="sendData"
                      className="btn btn-lg btn-primary w-325px mb-5 hover-scale"
                      disabled={!isValid || !watch('category')}
                      isLoading={isSubmitting}
                    >
                      {t('templates.wa.create.save-btn')}
                      <Icon icon="ki-send ms-2" size="fs-1" />
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default CreateTemplatesWa;
